import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  Anchor,
  Button,
  Container,
  Fieldset,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import type { InviteDetails } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { MIN_PASSWORD_LENGTH, passwordProblems } from '../auth/password';
import { ErrorNote, ProblemList } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { roleLabel } from '../format';

/** Opened from the link in an invitation email, by someone who may not have an account yet. */
export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { status, user, refresh, selectOrg } = useAuth();
  const navigate = useNavigate();

  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [gone, setGone] = useState(token === '');
  const [problem, setProblem] = useState<string | null>(null);
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (token === '') return;
    let cancelled = false;
    api
      .invite(token)
      .then((body) => {
        if (!cancelled) setInvite(body);
      })
      .catch(() => {
        if (!cancelled) setGone(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const join = useCallback(
    async (credentials: { firstName: string; lastName: string; password: string } | null) => {
      setProblem(null);
      setBusy(true);
      try {
        const result = await api.acceptInvite(token, credentials);
        try {
          await refresh();
          selectOrg(result.org_id);
          void navigate('/interactions', { replace: true });
        } catch {
          void navigate('/login', { replace: true });
        }
      } catch (error) {
        setProblem(errorMessage(error));
        setBusy(false);
      }
    },
    [navigate, refresh, selectOrg, token],
  );

  if (gone) {
    return (
      <Container component="main" size={460} pt={80} pb={64}>
        <Title order={1}>This invitation is no longer open</Title>
        <Text size="lg" mt="sm">
          It has expired, or it has already been used.
        </Text>
        <Text mt="md">Ask whoever invited you to send a new one. Invitations last seven days.</Text>
        <Text c="dimmed" size="sm" mt="lg">
          <Anchor component={Link} to="/login" size="sm">
            Sign in
          </Anchor>{' '}
          if you already have an account.
        </Text>
      </Container>
    );
  }

  if (invite === null || status === 'loading') {
    return (
      <Container component="main" size={460} pt={80} pb={64}>
        <Title order={1}>Invitation</Title>
        <Loading label="Checking the invitation" />
      </Container>
    );
  }

  const heading = `Join ${invite.org_name} on quizee`;
  const asRole = roleLabel(invite.role).toLowerCase();

  if (status === 'signed_in' && user !== null) {
    return (
      <Container component="main" size={460} pt={80} pb={64}>
        <Title order={1}>{heading}</Title>
        <Text size="lg" mt="sm">
          You have been invited to join {invite.org_name} as an {asRole}.
        </Text>
        <Text mt="md">
          You are signed in as {user.email}. That is the account that will join.
          {user.email.toLowerCase() !== invite.email.toLowerCase() && (
            <> The invitation was sent to {invite.email}, which is a different address.</>
          )}
        </Text>
        {problem !== null && <ErrorNote message={problem} />}
        <Group mt="lg">
          <Button disabled={busy} onClick={() => void join(null)}>
            {busy ? 'Joining...' : `Join ${invite.org_name}`}
          </Button>
        </Group>
      </Container>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const found = passwordProblems(password, confirmation);
    setProblems(found);
    if (found.length > 0) return;
    await join({ firstName: firstName.trim(), lastName: lastName.trim(), password });
  }

  return (
    <Container component="main" size={460} pt={80} pb={64}>
      <Title order={1}>{heading}</Title>
      <Text size="lg" mt="sm">
        You have been invited to join {invite.org_name} as an {asRole}. Set up your account and you
        are in.
      </Text>
      <Text c="dimmed" size="sm" mt="xs" mb="lg">
        The invitation was sent to {invite.email}.
      </Text>
      {problem !== null && <ErrorNote message={problem} />}
      <ProblemList problems={problems} />
      <form onSubmit={(event) => void submit(event)}>
        <Fieldset variant="unstyled" disabled={busy}>
          <Stack gap="md">
            {/*
             * Two fields, as everywhere else a person is created. A last name is optional, so
             * somebody who goes by one name can accept an invitation without inventing another.
             */}
            <TextInput
              id="invite-first-name"
              label="First name"
              autoComplete="given-name"
              required
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
            <TextInput
              id="invite-last-name"
              label="Last name"
              description="Optional."
              autoComplete="family-name"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
            <PasswordInput
              id="invite-password"
              label="Password"
              description={`At least ${String(MIN_PASSWORD_LENGTH)} characters.`}
              autoComplete="new-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <PasswordInput
              id="invite-password-again"
              label="Password again"
              autoComplete="new-password"
              required
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
            <Button type="submit" mt="xs">
              {busy ? 'Joining...' : 'Create your account and join'}
            </Button>
          </Stack>
        </Fieldset>
      </form>
      <Text c="dimmed" size="sm" mt="lg">
        Already have an account?{' '}
        <Anchor component={Link} to="/login" size="sm">
          Sign in
        </Anchor>{' '}
        first, then open this link again.
      </Text>
    </Container>
  );
}
